import tmi from 'tmi.js';

class TwitchChatService {
  constructor(channelName) {
    this.channelName = channelName;
    this.client = null;
    this.votes = { A: 0, B: 0, C: 0, D: 0 };
    this.voters = new Map(); // Para rastrear qué opción votó cada usuario
    this.voteCallbacks = [];
    this.isConnected = false; // Estado de conexión inicializado
    this.opcionesRespuesta = []; // Opciones de respuesta inicializadas
    this.connectionAttempts = 0; // Contador de intentos de conexión
    this.maxConnectionAttempts = 3; // Máximo de intentos de conexión
    this.connectionTimeout = null; // Para manejar reintentos de conexión
    this.reconnectDelay = 5000; // Retraso para reconexión en ms
    this.isPollingActive = false; // Control para saber si se está contando votos activamente
  }

  // Inicializar la conexión con el chat de Twitch
  initialize() {
    // Configuración del cliente de TMI.js
    const options = {
      options: { debug: false }, // Desactivar debug para reducir logs
      connection: {
        reconnect: false, // Desactivar reconexión automática para tener control manual
        secure: true
      },
      channels: [this.channelName]
    };

    // Crear el cliente de chat
    this.client = new tmi.client(options);

    // Registrar los eventos
    this.client.on('message', this.handleMessage.bind(this));
    this.client.on('connected', this.handleConnected.bind(this));
    this.client.on('disconnected', this.handleDisconnected.bind(this));
    this.client.on('error', this.handleError.bind(this));
  }

  // Manejar errores de conexión
  handleError(error) {
    console.error('Error en la conexión de Twitch:', error);
    this.isConnected = false;
    
    // Solo intentar reconexión si se está en modo de conteo activo
    if (this.isPollingActive && this.connectionAttempts < this.maxConnectionAttempts) {
      this.connectionAttempts++;
      console.log(`Reintentando conexión (${this.connectionAttempts}/${this.maxConnectionAttempts})...`);
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
      }
      this.connectionTimeout = setTimeout(() => {
        if (this.client && this.isPollingActive) {
          this.connect();
        }
      }, this.reconnectDelay);
    } else {
      console.log('Se ha alcanzado el número máximo de intentos de conexión o el conteo ha finalizado.');
    }
  }

  // Manejar mensajes del chat
  handleMessage(channel, tags, message, self) {
    // Ignorar mensajes del bot
    if (self) return;

    // Solo procesar mensajes si estamos en modo de conteo activo
    if (!this.isPollingActive) return;

    try {
      // Convertir mensaje a mayúsculas para hacer coincidencias más fáciles
      const msg = message.toUpperCase().trim();
      // Usar un identificador de usuario más consistente
      const userId = tags['user-id'] || tags.id || tags.username || tags['display-name'] || 'unknown';
      
      // Verificar si el mensaje es un voto directo (A, B, C, D)
      if (msg === 'A' || msg === 'B' || msg === 'C' || msg === 'D') {
        // Verificar si el usuario ya había votado anteriormente
        const votoAnterior = this.voters.get(userId);
        if (votoAnterior && votoAnterior !== msg) {
          // Si el usuario ya había votado por una opción diferente, restar su voto anterior
          this.votes[votoAnterior]--;
        } else if (votoAnterior === msg) {
          // Si el usuario está votando por la misma opción, no hacer nada (evitar voto duplicado)
          return;
        }
        // Registrar el nuevo voto
        this.votes[msg]++;
        // Registrar que este usuario ha votado por esta opción
        this.voters.set(userId, msg);
        this.notifyVoteUpdate();
      } else if (this.opcionesRespuesta && this.opcionesRespuesta.length > 0) {
        // Verificar si el mensaje coincide con alguna de las opciones de respuesta
        for (let i = 0; i < this.opcionesRespuesta.length; i++) {
          const opcion = this.opcionesRespuesta[i];
          // Verificar coincidencia exacta o parcial con la opción de respuesta
          if (opcion && (msg.includes(opcion.toUpperCase()) || opcion.toUpperCase().includes(msg))) {
            // Determinar la letra correspondiente a esta opción
            const letraOpcion = String.fromCharCode(65 + i); // A=65, B=66, C=67, D=68
            if (this.votes[letraOpcion] !== undefined) {
              // Verificar si el usuario ya había votado anteriormente
              const votoAnterior = this.voters.get(userId);
              if (votoAnterior && votoAnterior !== letraOpcion) {
                // Si el usuario ya había votado por una opción diferente, restar su voto anterior
                this.votes[votoAnterior]--;
              } else if (votoAnterior === letraOpcion) {
                // Si el usuario está votando por la misma opción, no hacer nada (evitar voto duplicado)
                return;
              }
              // Registrar el nuevo voto
              this.votes[letraOpcion]++;
              // Registrar que este usuario ha votado por esta opción
              this.voters.set(userId, letraOpcion);
              this.notifyVoteUpdate();
              break; // Solo contar el voto una vez
            }
          }
        }
      }
    } catch (error) {
      console.error('Error al manejar mensaje del chat:', error);
    }
  }

  // Manejar conexión exitosa
  handleConnected(address, port) {
    console.log(`Conectado al chat de Twitch: ${address}:${port}`);
    this.isConnected = true;
    this.connectionAttempts = 0; // Reiniciar contador de intentos al conectar exitosamente
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  // Manejar desconexión
  handleDisconnected(reason) {
    console.log(`Desconectado del chat de Twitch: ${reason}`);
    this.isConnected = false;
    // Limpiar el estado de conexión por si acaso
    if (this.client && this.client.ws) {
      this.client.ws = null;
    }
  }

 // Conectar al chat
  connect() {
    // Verificar si ya hay una conexión en proceso o activa
    if (this.isConnected || !this.client) {
      return;
    }

    // Solo conectar si estamos en modo de conteo activo
    if (!this.isPollingActive) {
      return;
    }

    try {
      // Verificar si ya hay una conexión en proceso
      if (this.client.ws) {
        const readyState = this.client.ws.readyState;
        if (readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN) {
          this.isConnected = true;
          return;
        }
      }

      // Conectar directamente sin setTimeout para evitar problemas de concurrencia
      this.client.connect().catch(error => {
        console.log('Error al conectar al chat de Twitch:', error.message);
        this.handleError(error);
      });
      this.isConnected = true;
    } catch (error) {
      console.log('Error al conectar al chat de Twitch:', error.message);
      this.handleError(error);
    }
 }

 // Desconectar del chat
  disconnect() {
    // Cancelar cualquier timeout pendiente
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    this.connectionAttempts = 0; // Reiniciar contador de intentos

    // Verificar si ya está desconectado
    if (!this.client) {
      return;
    }

    try {
      if (typeof this.client.disconnect === 'function' && this.client.ws) {
        // Verificar que el socket esté en estado abierto antes de desconectar
        const readyState = this.client.ws.readyState;
        if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
          this.client.disconnect().catch(error => {
            console.log('Error al desconectar del chat de Twitch:', error.message);
          });
        }
      }
      this.isConnected = false;
    } catch (error) {
      console.log('Error al desconectar del chat de Twitch:', error.message);
      this.isConnected = false;
    }
  }

  // Obtener conteo actual de votos
  getVoteCounts() {
    // Asegurar que los votos sean números válidos
    return {
      A: Number(this.votes.A) || 0,
      B: Number(this.votes.B) || 0,
      C: Number(this.votes.C) || 0,
      D: Number(this.votes.D) || 0
    };
  }

   // Obtener porcentajes de votos
  getVotePercentages() {
    // Asegurar que todos los valores sean números válidos
    const votes = this.getVoteCounts();
    const total = votes.A + votes.B + votes.C + votes.D;
    
    if (total === 0) {
      return { A: 0, B: 0, C: 0, D: 0 };
    }
    
    // Calcular porcentajes asegurando que sean números válidos
    const pctA = Math.round((votes.A / total) * 100);
    const pctB = Math.round((votes.B / total) * 100);
    const pctC = Math.round((votes.C / total) * 100);
    const pctD = Math.round((votes.D / total) * 100);
    
    // Validar que los porcentajes sean números finitos y no negativos
    return {
      A: Number.isFinite(pctA) && pctA >= 0 ? pctA : 0,
      B: Number.isFinite(pctB) && pctB >= 0 ? pctB : 0,
      C: Number.isFinite(pctC) && pctC >= 0 ? pctC : 0,
      D: Number.isFinite(pctD) && pctD >= 0 ? pctD : 0
    };
 }

  // Agregar callback para notificaciones de votos
  addVoteCallback(callback) {
    if (typeof callback === 'function') {
      this.voteCallbacks.push(callback);
    }
  }

   // Notificar actualizaciones de votos
  notifyVoteUpdate() {
    try {
      const voteData = {
        counts: this.getVoteCounts(),
        percentages: this.getVotePercentages(),
        total: Object.values(this.getVoteCounts()).reduce((sum, count) => sum + count, 0)
      };
      
      // Notificar a todos los callbacks registrados
      // Hacemos una copia del array para evitar problemas si se modifican los callbacks durante la ejecución
      [...this.voteCallbacks].forEach(callback => {
        try {
          if (typeof callback === 'function') {
            callback(voteData);
          }
        } catch (error) {
          console.error('Error al notificar actualización de votos:', error);
        }
      });
    } catch (error) {
      console.error('Error en notifyVoteUpdate:', error);
    }
  }

  // Reiniciar los votos manteniendo las opciones de respuesta
  resetVotes() {
    this.votes = { A: 0, B: 0, C: 0, D: 0 };
    this.voters.clear();
    // No limpiar opcionesRespuesta aquí para mantener las opciones establecidas
    // this.opcionesRespuesta = [];
    // No limpiar callbacks aquí para evitar perder los callbacks registrados
    // this.voteCallbacks = []; // Limpiar callbacks para evitar duplicados
    this.notifyVoteUpdate();
  }

  // Establecer las opciones de respuesta actuales
  setOpcionesRespuesta(opciones) {
    this.opcionesRespuesta = Array.isArray(opciones) ? opciones : [];
  }

  // Activar el modo de conteo de votos
  startPolling() {
    this.isPollingActive = true;
    // Reiniciar votos al comenzar para que cada uso del comodín empiece con conteo limpio
    this.resetVotes(); // Reiniciar votos al comenzar
    if (!this.isConnected) {
      this.connect();
    }
  }

  // Establecer votos iniciales sin reiniciar otros estados
 setInitialVotes(initialVotes) {
    // Asegurar que los votos iniciales sean válidos
    this.votes = { 
      A: (initialVotes && typeof initialVotes.A === 'number') ? initialVotes.A : 0,
      B: (initialVotes && typeof initialVotes.B === 'number') ? initialVotes.B : 0,
      C: (initialVotes && typeof initialVotes.C === 'number') ? initialVotes.C : 0,
      D: (initialVotes && typeof initialVotes.D === 'number') ? initialVotes.D : 0
    };
    // Actualizar los porcentajes basados en los votos iniciales
    this.notifyVoteUpdate();
  }

  // Detener el modo de conteo de votos
  stopPolling() {
    this.isPollingActive = false;
    // Cancelar cualquier timeout pendiente
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    // No desconectar inmediatamente, dejar que se complete cualquier operación pendiente
    setTimeout(() => {
      if (!this.isPollingActive) {
        this.disconnect();
      }
    }, 1000); // Esperar 1 segundo antes de desconectar
  }
}

export default TwitchChatService;
